import {
  Badge,
  Chip,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import { uniq, pullAll, reverse } from "lodash";
import { routeTitle } from "./LeftMenu";
import LogoText from "./LogoText";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getOptions } from "../models/report.model";
import { useConfig } from "../containers/ConfigContext";
import { LanguageMenuItem } from "./SwitchLanguageButton";
import DownloadRecords from "./DownloadRecords";
import { parseImageUrl } from "../lib/helper";
import NotificationMenuItem from "./NotificationMenuItem";
import { getNotifications } from "../actions/notification.action";
import { INotification, NotificationStatus } from "../models/notification.model";

export default function TopHeader(props: { onSwitchLanguage: (code: string) => void; print?: boolean }) {
  const { onSwitchLanguage, print } = props;
  const location = useLocation();
  const [histories, setHistories] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const navigate = useNavigate();
  const { signOut, user } = useAuthenticator((context) => [context.user]);
  const { localized, loggedUser } = useConfig();
  const menuOpen = Boolean(anchorEl);
  const unreadCount = notifications.filter(
    (n) => n.status === NotificationStatus.unread
  ).length;

  useEffect(() => {
    histories.unshift(location.pathname);
    setHistories(uniq(histories));
    window.scroll(0, 0);
  }, [location]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const result = await getNotifications();
        setNotifications(result.notifications || []);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    // Initial fetch
    fetchNotifications();

    // Set up interval to fetch every 10 seconds
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);

  const handleClick = (route: string) => {
    navigate(route);
  };

  const handleDelete = (route: string) => {
    const a = pullAll(histories, [route]);
    setHistories([...a]);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    handleCloseMenu();
    signOut();
  };

  return (
    <Stack direction="column" sx={{ mb: 4, px: 4 }}>
      <Grid container sx={{ pb: 2 }} alignItems={"center"}>
        <Grid item container xs={8}>
          <LogoCurrentHierarchy />
        </Grid>
        {!print && (
          <Grid item container xs={4} justifyContent={"flex-end"}>
            <Stack spacing={4}>
              <Stack direction={"row"} justifyContent={"flex-end"}>
                <LogoText />
              </Stack>
              <Stack direction={"row"} justifyContent={"flex-end"} alignItems={"center"}>
                <IconButton onClick={handleOpenMenu} sx={{ p: 0 }}>
                  <Badge badgeContent={unreadCount} color="success">
                    <Avatar alt={loggedUser?.name || user?.username || "User"} sx={{ width: 40, height: 40 }}>
                      {(loggedUser?.name || user?.username || "U").charAt(0).toUpperCase()}
                    </Avatar>
                  </Badge>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  sx={{ mt: 1 }}
                >
                  <LanguageMenuItem onSwitchLanguage={onSwitchLanguage} />
                  <DownloadRecords />
                  <NotificationMenuItem
                    notifications={notifications}
                    setNotifications={setNotifications}
                  />
                  <Divider />
                  <MenuItem onClick={handleSignOut}>
                    <ListItemIcon>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText>{localized("Log Out")}</ListItemText>
                  </MenuItem>
                </Menu>
              </Stack>
            </Stack>
          </Grid>
        )}
      </Grid>
      <Stack direction={"row"} flexWrap={"wrap"} sx={{ pb: 1, mb: 2 }}>
        {reverse([...histories].slice(1)).map((h) => (
          <Chip
            key={h}
            sx={{ mr: 1, mb: 1 }}
            label={localized(h.startsWith("/charts") ? "Chart" : routeTitle[h])}
            variant="outlined"
            onClick={() => handleClick(h)}
            onDelete={() => handleDelete(h)}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export function LogoCurrentHierarchy() {
  const { loggedUser, filter, localized } = useConfig();
  const logo = parseImageUrl(loggedUser?.company?.logo);
  const options = getOptions(loggedUser);
  const theme = useTheme();
  const name = filter?.hierarchyFilter
    ? options.find((a) => a.value === filter?.hierarchyFilter)?.label
    : options[0]?.label;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <Stack
      direction={!isMobile ? "row" : "column"}
      spacing={!isMobile ? 4 : 0}
      alignItems={!isMobile ? "center" : "flex-start"}
    >
      <Link to="/">
        {logo && (
          <img
            src={logo}
            alt="logo"
            style={{
              height: isMobile ? 60 : 80,
              borderRadius: 8,
              objectFit: "contain",
            }}
          />
        )}
      </Link>
      <Typography variant={!isMobile ? "h4" : "h6"}>{name ? localized(name) : name}</Typography>
    </Stack>
  );
}
